import { c as _c } from "react/compiler-runtime";
import { MemberProfileEmptyPage } from "@components/common/empty-page/member-profile-empty-page";
import { MemberAccessSummary } from "@components/common/member/member-access-summary";
import { MemberGroupsTable } from "@components/common/member/member-groups-table";
import { MemberProfileSkeleton } from "@components/common/skeleton-loading/member-profile-skeleton";
import { MemberProfileInfoBar } from "@components/member-profile/member-profile-info-bar";
import { UserInfo } from "@components/user-info/user-info";
import { isCompanyMemberDetails } from "@custom-types/member-type-guards";
import { useDynamicHeight } from "@hooks/use-dynamic-height";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useMemberGroups } from "@hooks/use-member-groups";
import { Box, Grid, Stack } from "@mui-wrapper";
import { TeamDetailsInfoBar } from "@pages/members/teams/team-details/team-details-info-bar";
import { NotFoundPage } from "@pages/not-found-page";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { resetCompanyMemberGroupsState } from "@store/company-member-groups/company-member-groups-slice";
import { useAppDispatch } from "@store/store-helper";
import { useEffect } from "react";
/**
 * Page to show the overview of the profile of the selected user.
 */
export function MemberOverviewPage(t0) {
  const $ = _c(34);
  const {
    member,
    team,
    isLoading
  } = t0;
  const dispatch = useAppDispatch();
  const {
    groups,
    companyMemberGroupsStatus
  } = useMemberGroups();
  const {
    isScreenMdOrSmaller
  } = useMediaQueryList();
  let t1;
  if ($[0] !== isScreenMdOrSmaller) {
    t1 = {
      shouldSkip: isScreenMdOrSmaller
    };
    $[0] = isScreenMdOrSmaller;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    ref: groupsContainerRef,
    height: dynamicHeight
  } = useDynamicHeight(t1);
  const isFetching = isLoading || companyMemberGroupsStatus === "pending";
  let t2;
  let t3;
  if ($[2] !== dispatch) {
    t2 = () => () => {
      dispatch(resetCompanyMemberGroupsState());
    };
    t3 = [dispatch];
    $[2] = dispatch;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (isFetching || companyMemberGroupsStatus === "uninitialized") {
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <MemberProfileSkeleton />;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (!member || !isCompanyMemberDetails(member) || companyMemberGroupsStatus !== "succeeded") {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <NotFoundPage />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  const t4 = !FaroCoreApiUtils.Member.isTeam(member) ? "MEMBER_PROFILE_OVERVIEW_CONTAINER" : "TEAM_PROFILE_OVERVIEW_CONTAINER";
  let t5;
  if ($[7] !== member) {
    t5 = FaroCoreApiUtils.Member.isTeam(member) ? <TeamDetailsInfoBar /> : <MemberProfileInfoBar member={member} />;
    $[7] = member;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      display: "flex",
      flexDirection: {
        xs: "column-reverse",
        lg: "row"
      },
      width: "100%",
      gap: "20px"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      lg: "1 1 0",
      xs: "unset"
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== dynamicHeight) {
    t8 = {
      flex: t7,
      minWidth: 0,
      maxHeight: {
        lg: dynamicHeight,
        xs: "100%"
      },
      overflowY: "auto",
      marginTop: "14px"
    };
    $[11] = dynamicHeight;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== member) {
    t9 = <MemberAccessSummary member={member} />;
    $[13] = member;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== groups || $[16] !== member) {
    t10 = groups.length === 0 ? <MemberProfileEmptyPage member={member} /> : <MemberGroupsTable groups={groups} member={member} />;
    $[15] = groups;
    $[16] = member;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== groupsContainerRef || $[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = <Box ref={groupsContainerRef} component="div" sx={t8}>{t9}{t10}</Box>;
    $[18] = groupsContainerRef;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      marginTop: "14px",
      alignItems: "center"
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== member || $[25] !== team) {
    t13 = <Stack sx={t12}><UserInfo member={member} team={team} /></Stack>;
    $[24] = member;
    $[25] = team;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <Box sx={t6}>{t11}{t13}</Box>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== t4 || $[32] !== t5) {
    t15 = <Grid data-testid={t4}>{t5}{t14}</Grid>;
    $[30] = t14;
    $[31] = t4;
    $[32] = t5;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}